import { useEffect, useState } from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Chip, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { SectionCard, Row } from '../../shared/Panel';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { useFeature } from '../featureFlags/FeatureGate';
import {
  FEATURE_TRANSPOREON,
  fetchTransporeonForOrder,
  matchLabel,
  qualifierLabel,
  type TransporeonOrderItem,
} from './transporeonApi';

/**
 * Sekcja „Transporeon” na karcie zlecenia (04.11.2026, przełącznik
 * `transporeonImport`) — tylko do odczytu. Dane z Transporeonu, osobno od
 * danych z TMS: stan transportu z ostatniego komunikatu, punkty z oknami
 * i historia komunikatów. Bez danych osobowych i bez surowego komunikatu
 * (ten tylko w sekcji administracyjnej). Zlecenie bez transportu
 * z Transporeonu — sekcji nie ma.
 */
export const TRANSPOREON_SECTION_NOTE =
  'Dane z Transporeonu (zleceniodawcy) — osobno od danych z TMS. Tylko do odczytu: nic stąd nie idzie do Transporeonu, TMS ani VIA.';

const pointText = (p: TransporeonOrderItem['points'][number]) =>
  [[p.country, p.zip].filter(Boolean).join(' '), p.city].filter(Boolean).join(' ') || '—';

export const TransporeonSection = () => {
  const record = useRecordContext();
  const on = useFeature(FEATURE_TRANSPOREON);
  const [items, setItems] = useState<TransporeonOrderItem[]>([]);
  const id = record?.id;

  useEffect(() => {
    if (!on || id === undefined || id === null) return;
    let alive = true;
    fetchTransporeonForOrder(id)
      .then((r) => alive && setItems(r.items))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [on, id]);

  if (!on || items.length === 0) return null;
  return (
    <SectionCard title="Transporeon">
      <Box data-testid="transporeon-section">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {TRANSPOREON_SECTION_NOTE}
        </Typography>
        {items.map((t) => (
          <Stack key={t.id} spacing={1} sx={{ mb: 2 }} data-testid={`transporeon-transport-${t.number}`}>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip size="small" label="źródło: Transporeon" variant="outlined" />
              {t.cancelledAt && <Chip size="small" color="error" label="anulowany w Transporeonie" />}
              {t.reissuedAt && <Chip size="small" color="warning" label="wystawiony ponownie" />}
              {t.matchStatus === 'multiple' && <Chip size="small" color="warning" label="kilka zleceń w TMS" />}
            </Stack>
            <Table size="small">
              <TableBody>
                <Row label="Transport">
                  {t.number}
                  {t.shipperName ? ` · ${t.shipperName}` : ''}
                  {t.tpwLink && (
                    <>
                      {' · '}
                      <Link href={t.tpwLink} target="_blank" rel="noopener noreferrer">
                        otwórz w Transporeonie
                      </Link>
                    </>
                  )}
                </Row>
                <Row label="Ostatni komunikat">
                  {qualifierLabel(t.lastQualifier)} · {dateTimeNoSeconds(t.lastMessageAt)}
                </Row>
                <Row label="Dopasowanie do TMS">{t.matchNote ? `${matchLabel(t.matchStatus)} — ${t.matchNote}` : matchLabel(t.matchStatus)}</Row>
                {t.weightKg !== null && <Row label="Waga">{Math.round(t.weightKg)} kg</Row>}
              </TableBody>
            </Table>
            <Box sx={TABLE_SCROLL_SX}>
              <Table size="small" aria-label="Punkty w Transporeonie">
                <TableHead>
                  <TableRow>
                    <TableCell>Punkt</TableCell>
                    <TableCell>Miejsce</TableCell>
                    <TableCell>Okno (czas polski)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {t.points.map((p, i) => (
                    <TableRow key={`${p.type}-${i}`}>
                      <TableCell>{p.type === 'loading' ? 'załadunek' : p.type === 'unloading' ? 'rozładunek' : p.type}</TableCell>
                      <TableCell>{pointText(p)}</TableCell>
                      <TableCell>
                        {dateTimeNoSeconds(p.from)} – {dateTimeNoSeconds(p.until)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Historia komunikatów:{' '}
              {t.history
                .map(
                  (h) =>
                    `${dateTimeNoSeconds(h.messageAt)} ${qualifierLabel(h.qualifier)}${h.outcome === 'older' ? ' (starszy — tylko historia)' : ''}${h.alertsCreated > 0 ? ' · alert' : ''}`,
                )
                .join('; ') || '—'}
            </Typography>
          </Stack>
        ))}
      </Box>
    </SectionCard>
  );
};
